import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { ADMIN } from 'services/consts';

import AppLogger from 'utils/AppLogger';

const UpdateTranslationModal = NiceModal.create((props) => {
	const { id } = props;
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const modal = useModal();
	const dispatch = useDispatch();

	const schema = useMemo(
		() =>
			yup.object().shape({
				dcResponse: yup.string().nullable(),
				aiResponseEN: yup.string().nullable(),
				aiResponseTR: yup.string().nullable(),
				aiResponseAR: yup.string().nullable()
			}),
		[]
	);

	const fillForm = useCallback(async () => {
		if (!id) {
			return;
		}
		const defaultValues = {
			dcResponse: null,
			aiResponseEN: null,
			aiResponseTR: null,
			aiResponseAR: null
		};
		try {
			const response = await axiosPrivate.post(ADMIN.GET_DC_AI_ERROR, {
				id
			});
			if (response.status === 200) {
				return {
					...defaultValues,
					dcResponse: response.data.dcResponse ?? null,
					aiResponseEN: response.data.aiResponseEN ?? null,
					aiResponseTR: response.data.aiResponseTR ?? null,
					aiResponseAR: response.data.aiResponseAR ?? null
				};
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
			return defaultValues;
		}
	}, []);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		formState: { isDirty, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;

	const handleCloseModal = () => {
		modal.remove();
	};

	const onSubmit = async (data) => {
		try {
			const response = await axiosPrivate.post(ADMIN.UPDATE_DC_AI_ERROR_LOGS, {
				id,
				aiResponseTR: data.aiResponseTR,
				aiResponseEN: data.aiResponseEN,
				aiResponseAR: data.aiResponseAR
			});
			if (response.status === 200) {
				showSuccess();
				dispatch(updateDynamicListsInPage());
				handleCloseModal();
			} else {
				AppLogger.error(response);
				showError();
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	return (
		<Modal fullwidth maxWidth="small" className="update-translation-modal">
			<ModalTitle>
				<Typography variant="p1">updateTranslation</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="updateTranslation.dcError" isLoading={isLoading}>
						<TextArea maxlength={4096} rows="2" name="dcResponse" disabled />
					</FormItem>
					<FormItem label="english" isLoading={isLoading}>
						<TextArea maxlength={4096} rows="3" name="aiResponseEN" />
					</FormItem>
					<FormItem label="turkish" isLoading={isLoading}>
						<TextArea maxlength={4096} rows="3" name="aiResponseTR" />
					</FormItem>
					<FormItem label="arabic" isLoading={isLoading}>
						<TextArea maxlength={4096} rows="3" name="aiResponseAR" />
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				<Button
					label="update"
					disabled={!isDirty}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.error(errors);
					})}
					loading={isLoading}
				/>
			</ModalActions>
		</Modal>
	);
});

export default UpdateTranslationModal;
